import React, { useState, useRef } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import FaqItem from "./FaqItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { keyframes } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { generateTestId, getMediaPath } from "../../../app/functions";

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.brands.text.active,
  color: theme.palette.background.paper,
  borderRadius: "24px",
  padding: "18px 40px",
  width: 325,
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: 1.08,
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.25),
  transition: "all 0.3s ease",
  direction: "ltr",
  "& img": {
    width: 24,
    height: 24,
    order: -1,
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "325px",
    padding: "14px 32px",
    fontSize: "17px",
  },
}));

const ShowMoreButton = styled(Button)(({ theme }) => ({
  backgroundColor: "transparent",
  color: theme.palette.text.answer,
  borderRadius: "24px",
  padding: "12px 32px",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: 1.08,
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  marginTop: theme.spacing(4),
  transition: "all 0.5s ease",
  position: "relative",
  direction: "rtl",
  "&:hover": {
    backgroundColor: "rgba(62, 60, 159, 0.04)",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(62, 60, 159, 0.2)",
  },
  "& .MuiIconHot-root": {
    fontSize: "32px",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 4px rgba(62, 60, 159, 0.1)",
  },
  [theme.breakpoints.up("sm")]: {
    marginTop: "0px",
    fontSize: "24px",
  },
}));

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  maxWidth: "1920px",
  padding: "40px 24px",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  direction: "rtl",
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(8),
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  direction: "rtl",
  padding: "0 0px",
  [theme.breakpoints.up("sm")]: {
    gap: "20px",
  },
  [theme.breakpoints.up("md")]: {
    gap: "40px",
    padding: "0 100px",
  },
  [theme.breakpoints.up("lg")]: {
    padding: "0 120px",
  },
  [theme.breakpoints.up("xl")]: {
    padding: "0 180px",
  },
}));

const QuestionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "stretch",
  gap: "1px",
  width: "100%",
  direction: "rtl",
  flexDirection: "column",
  border: "1px solid #D8D8EC",
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    border: "none",
  },
  [theme.breakpoints.up("md")]: {
    gap: "40px",
  },
  [theme.breakpoints.up("lg")]: {
    gap: "60px",
  },
  [theme.breakpoints.up("xl")]: {
    gap: "80px",
  },
}));

const Column = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "18px",
  flex: 1,
  width: "100%",
  direction: "rtl",
  [theme.breakpoints.up("sm")]: {
    gap: "24px",
  },
  [theme.breakpoints.up("md")]: {
    gap: "30px",
  },
}));

const ButtonsWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(2),
  direction: "rtl",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    width: "100%",
    alignItems: "center",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "40px 24px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "48px 28px",
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  direction: "rtl",
  textAlign: "center",
  fontSize: "24px",
  fontWeight: 700,
  lineHeight: "normal",
  [theme.breakpoints.up("sm")]: {
    fontSize: "32px",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "36px",
  },
  [theme.breakpoints.up("lg")]: {
    fontSize: "40px",
  },
}));

const FaqSection = ({ data, testID = "faq-section" }) => {
  if(!data?.questions?.length) return null;
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const theme = useTheme();

  const handleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const midPoint = Math.floor(data.questions?.length / 2) || 0;
  const leftColumnQuestions = data.questions?.slice(0, midPoint) || [];
  const rightColumnQuestions = data.questions?.slice(midPoint) || [];

  const visibleQuestions =
    isMobile && !showAllQuestions
      ? data.questions?.slice(0, 2)
      : data.questions;

  const handleShowMore = () => {
    setShowAllQuestions(true);
  };

  return (
    <Container testID={generateTestId(testID, "root")}>
      <ContentWrapper testID={generateTestId(testID, "content-wrapper")}>
        <StyledTypography
          variant="h1"
          sx={{
            color: theme.palette.text.answer,
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: "normal",
            [theme.breakpoints.up("sm")]: {
              fontSize: "28px",
              marginBottom: "10px",
            },
            [theme.breakpoints.up("md")]: {
              fontSize: "32px",
            },
            [theme.breakpoints.up("lg")]: {
              fontSize: "36px",
            },
          }}
          testID={generateTestId(testID, "title")}
        >
          <Typography variant="sectionTitle">{data.title}</Typography>
        </StyledTypography>
        {isMobile ? (
          <Column testID={generateTestId(testID, "mobileColumn")}>
            {visibleQuestions.map((faq, index) => (
              <FaqItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isExpanded={expandedIndex === index}
                onToggle={() => handleExpand(index)}
                testID={generateTestId(testID, `faq-item-${index}`)}
              />
            ))}
            {!showAllQuestions && data.questions.length > 2 && (
              <ShowMoreButton
                onClick={handleShowMore}
                testID={generateTestId(testID, "show-more-button")}
              >
                <KeyboardArrowDownIcon />
                {data.moreQuestions}
              </ShowMoreButton>
            )}
          </Column>
        ) : (
          <QuestionsContainer
            testID={generateTestId(testID, "questions-container")}
          >
            <Column testID={generateTestId(testID, "leftColumn")}>
              {leftColumnQuestions.map((faq, index) => (
                <FaqItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isExpanded={expandedIndex === index}
                  onToggle={() => handleExpand(index)}
                  testID={generateTestId(testID, `faqItem_${index}`)}
                />
              ))}
            </Column>
            <Column testID={generateTestId(testID, "rightColumn")}>
              {rightColumnQuestions.map((faq, index) => (
                <FaqItem
                  key={index + midPoint}
                  question={faq.question}
                  answer={faq.answer}
                  isExpanded={expandedIndex === index + midPoint}
                  onToggle={() => handleExpand(index + midPoint)}
                  testID={generateTestId(testID, `faqItem_${index + midPoint}`)}
                />
              ))}
            </Column>
          </QuestionsContainer>
        )}
      </ContentWrapper>
      <ButtonsWrapper testID={generateTestId(testID, "buttonsWrapper")}>
        <StyledButton
          onClick={(event) => { event.preventDefault(); window.open(data.emailLink, '_blank'); }}
          testID={generateTestId(testID, "mailButton")}
        >
          {data.emailText}
          <img src={getMediaPath(data.emailImage)} alt={data.emailText} />
        </StyledButton>
        <StyledButton
          onClick={(event) => { event.preventDefault(); window.open(data.whatsappLink, '_blank'); }}
          testID={generateTestId(testID, "whatsappButton")}
        >
          {data.whatsappText}
          <img src={getMediaPath(data.whatsappImage)} alt={data.whatsappText} />
        </StyledButton>
      </ButtonsWrapper>
    </Container>
  );
};

export default FaqSection;
